import { useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Search, X, ArrowLeft, Calendar, Clock, Scissors, User, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { buscarCitasParaCancelar, cancelarCitaPublica } from "@/lib/api";

export default function CancelPage() {
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [citas, setCitas] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [cancelando, setCancelando] = useState(null);

  const handleBuscar = async (e) => {
    e.preventDefault();
    if (!nombre.trim() || !telefono.trim()) return;
    setBuscando(true);
    setCitas(null);
    try {
      const res = await buscarCitasParaCancelar(nombre.trim(), telefono.trim());
      setCitas(res.data.citas || []);
    } catch {
      toast.error("Error al buscar citas");
    } finally {
      setBuscando(false);
    }
  };

  const handleCancelar = async (citaId) => {
    setCancelando(citaId);
    try {
      await cancelarCitaPublica(citaId, nombre.trim(), telefono.trim());
      toast.success("Cita cancelada correctamente");
      setCitas(prev => prev.filter(c => c.id !== citaId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo cancelar la cita");
    } finally {
      setCancelando(null);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 max-w-lg mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button asChild variant="ghost" size="icon">
          <Link to="/"><ArrowLeft className="h-5 w-5" /></Link>
        </Button>
        <h1 className="text-2xl font-heading">Cancelar cita</h1>
      </div>

      <Card className="mb-6">
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground mb-4">
            Introduce tu nombre y telefono para ver y cancelar tus citas
          </p>
          <form onSubmit={handleBuscar} className="space-y-3">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <Input placeholder="Tu nombre completo" value={nombre}
                onChange={e => setNombre(e.target.value)} className="h-12" data-testid="cancel-nombre" />
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <Input placeholder="Tu telefono (9 digitos)" type="tel" value={telefono}
                onChange={e => setTelefono(e.target.value)} className="h-12" data-testid="cancel-telefono" />
            </div>
            <Button type="submit" className="w-full h-12" disabled={!nombre.trim() || !telefono.trim() || buscando}
              data-testid="btn-buscar-cancelar">
              <Search className="h-4 w-4 mr-2" />
              {buscando ? "Buscando..." : "Buscar mis citas"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {citas !== null && citas.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">No tienes citas activas con esos datos</p>
          </CardContent>
        </Card>
      )}

      {citas && citas.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-medium">{citas.length} cita{citas.length > 1 ? "s" : ""} encontrada{citas.length > 1 ? "s" : ""}</p>
          {citas.map(cita => (
            <Card key={cita.id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Scissors className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{cita.servicio_nombre}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{format(parseISO(cita.fecha), "EEEE d 'de' MMMM", { locale: es })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{cita.hora_inicio} - {cita.hora_fin}</span>
                    </div>
                  </div>
                  <Badge>{cita.peluquero_nombre}</Badge>
                </div>
                <Button variant="destructive" size="sm" className="w-full"
                  disabled={cancelando === cita.id}
                  onClick={() => handleCancelar(cita.id)}
                  data-testid={`btn-cancelar-${cita.id}`}>
                  <X className="h-4 w-4 mr-1" />
                  {cancelando === cita.id ? "Cancelando..." : "Cancelar esta cita"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center mt-6">
        No se pueden cancelar citas con menos de 24h de antelacion
      </p>
    </div>
  );
}
